import { useSize } from 'ahooks'
import { useRouter } from 'next/router'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { When } from 'react-if'
import { CarrierInfoMarkDTO } from 'src/types'
import Copyright from './copyright'
import Footer from './footer'
import styles from './index.module.scss'
import NavbarNew from './navbar_new'
import Sidebar from './sidebar'

interface Props {
  children: ReactNode
  animation?: boolean
  showAllPlatform?: boolean
  carrierInfoMark?: CarrierInfoMarkDTO
  country?: string | any
}

export default function Layout(props: Props) {
  const { animation, showAllPlatform, carrierInfoMark } = props
  const [loading, setLoading] = useState<boolean>(true)
  const [isAdExpanded, setIsAdExpanded] = useState<boolean>(false)
  const [hasAdContent, setHasAdContent] = useState<boolean>(false)
  const observerRef = useRef<any>(null)
  const router = useRouter()
  const { pathname } = router

  // 监听窗口尺寸变化
  const windowSize = useSize(typeof document !== 'undefined' ? document.documentElement : undefined)
  const shouldShowAd =
    (!windowSize || (windowSize.height && windowSize.height >= 730)) &&
    pathname === '/' &&
    typeof window !== 'undefined' &&
    window.YQ?.country !== 'CN'

  useEffect(() => {
    setLoading(false)
  }, [])

  // 监听广告容器内容变化
  useEffect(() => {
    if (!shouldShowAd) return

    const checkAdContent = () => {
      const adContainer = document.getElementById('DA_V6-WWW-Bottom')
      if (adContainer) {
        // 检查容器是否有 iframe 标签（广告内容）
        const iframe = adContainer.querySelector('iframe')
        const hasContent = !!iframe

        if (hasContent) {
          // 有广告内容，展开
          setIsAdExpanded(true)
          observerRef.current?.disconnect()
          setTimeout(() => {
            setHasAdContent(true)
          }, 1000)
        } else {
          setHasAdContent(false)
        }
      }
    }

    // 延迟检查，等待广告加载
    const timer = setTimeout(checkAdContent, 1000)

    // 使用 MutationObserver 监听广告容器变化
    const adContainer = document.getElementById('DA_V6-WWW-Bottom')
    if (adContainer) {
      observerRef.current = new MutationObserver(() => {
        checkAdContent()
      })

      observerRef.current.observe(adContainer, {
        childList: true,
        subtree: true,
        characterData: true
      })

      return () => {
        observerRef.current.disconnect()
        clearTimeout(timer)
      }
    }

    return () => clearTimeout(timer)
  }, [shouldShowAd])

  // 当屏幕高度变化导致不应该显示广告时，重置状态
  useEffect(() => {
    if (!shouldShowAd && hasAdContent) {
      setHasAdContent(false)
      setIsAdExpanded(false)
    }
  }, [shouldShowAd, hasAdContent])

  const handleToggleAd = () => {
    setIsAdExpanded(!isAdExpanded)
  }
  return (
    <>
      <a
        href="#content"
        className="visually-hidden"
        style={{
          position: 'absolute',
          height: '1px',
          width: '1px',
          overflow: 'hidden',
          clip: 'rect(1px, 1px, 1px, 1px)',
          color: 'var(--primary-color)'
        }}
      >
        Skip to main content
      </a>
      {/* 灰度展示重构头部导航 */}
      <NavbarNew carrierInfoMark={carrierInfoMark} />

      <main suppressHydrationWarning className="relative">
        {props.children}
        <When condition={loading}>
          <div className={styles['loading-wrapper']}>
            <div className={styles.loading}></div>
            <div className={styles['loading-text']}>Loading</div>
          </div>
        </When>
      </main>
      <Footer animation={animation} showAllPlatform={showAllPlatform} />
      <Copyright />
      {/* 折叠式锚定广告 */}
      {shouldShowAd && (
        <>
          {/* 占位空间 - 避免内容被遮挡 */}
          <div
            className={`w-full transition-all duration-300 ${
              isAdExpanded && hasAdContent ? 'h-[80px] md:h-[120px] sm:h-[90px]' : hasAdContent ? 'h-[50px]' : 'h-0'
            }`}
          />

          {/* 折叠式锚定广告容器 */}
          <div
            className={`fixed bottom-0 left-1/2 -translate-x-1/2 z-[100] transition-all duration-300 ease-in-out w-[320px] sm:w-[468px] md:w-[728px] lg:w-[970px] xl:w-[1200px] ${
              isAdExpanded ? 'translate-y-0' : 'translate-y-full'
            }`}
            style={{
              transform:
                isAdExpanded && hasAdContent
                  ? 'translateX(-50%) translateY(0)'
                  : !hasAdContent
                    ? 'translateX(-50%) translateY(100%)'
                    : 'translateX(-50%) translateY(calc(100% - 28px))'
            }}
          >
            {/* 折叠条 - 点击展开/收起 */}
            {hasAdContent && (
              <div className="flex justify-center">
                <button
                  className="bg-white border-x border-t border-gray-300 cursor-pointer transition-colors rounded-t-md px-3 py-0.5 mb-[-1px]"
                  onClick={handleToggleAd}
                >
                  <svg
                    className={`w-6 h-6 text-gray-600 transition-transform duration-300 ${
                      isAdExpanded ? '' : 'rotate-180'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}

            {/* 广告内容区域 */}
            <div className="bg-white shadow-lg border-t border-gray-200 rounded-t-lg overflow-hidden">
              <div className="pt-2">
                <div
                  className="border border-gray-300 w-full h-[50px] sm:h-[60px] md:h-[90px] bg-gray-50 flex items-center justify-center text-gray-400"
                  id="DA_V6-WWW-Bottom"
                >
                  {/* 广告内容将在这里加载 */}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <Sidebar />
    </>
  )
}
